export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadJSON(json: string, filename: string) {
  downloadBlob(new Blob([json], { type: "application/json" }), filename);
}

/**
 * Open a file dialog and hand the chosen file to `onFile`.
 *
 * Returns a promise that settles when the WORK is done, not when the dialog
 * opens: it resolves `true` once `onFile`'s own promise resolves (or
 * immediately, if it returns nothing), `false` if the user cancelled, and
 * **rejects with whatever `onFile` threw**. A caller can therefore hold a
 * spinner for the real duration and surface the failure with a plain
 * `.catch()` — no try/catch-and-rethrow inside the callback.
 *
 * The old version returned `undefined` the instant `.click()` was called, so
 * every `await pickFile(...)` resolved before the user had even picked a file
 * and every busy state wrapped around it was dead on arrival.
 *
 * Cancellation is detected via the `cancel` event where supported, falling back
 * to the window regaining focus — neither fires reliably everywhere, so the
 * promise also resolves if `change` arrives with no file.
 */
export function pickFile(
  accept: string,
  onFile: (file: File) => void | Promise<void>,
): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = accept;
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("focus", onFocus);
      fn();
    };
    const done = (picked: boolean) => finish(() => resolve(picked));
    const fail = (err: unknown) => finish(() => reject(err));
    // Focus returns to the window when the dialog closes either way; `change`
    // fires first when a file WAS picked, so a short grace period keeps a slow
    // change event from being misread as a cancel.
    const onFocus = () => setTimeout(() => done(false), 400);

    inp.onchange = () => {
      const f = inp.files?.[0];
      if (!f) return done(false);
      let r: void | Promise<void>;
      try {
        r = onFile(f);
      } catch (e) {
        return fail(e);
      }
      if (r && typeof (r as Promise<void>).then === "function") {
        (r as Promise<void>).then(() => done(true), fail);
      } else {
        done(true);
      }
    };
    inp.addEventListener("cancel", () => done(false));
    window.addEventListener("focus", onFocus, { once: true });
    inp.click();
  });
}
